import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useChatInbox } from '../../contexts/ChatInboxContext';
import { unreadBadgeLabel } from '../../pages/friends/FriendsSubNav';
import { trackUxEvent } from '../../services/uxEvents';
import { triggerShakeOnce } from '../../utils/triggerShakeOnce';
import eventsActive from './assets/events.png';
import eventsInactive from './assets/eventsBlack.png';
import placesActive from './assets/favorites.png';
import placesInactive from './assets/favoritesBlack.png';
import mapActive from './assets/map.png';
import mapInactive from './assets/mapblack.png';
import profileActive from '../../assets/icons/profile.png';
import profileInactive from '../../assets/icons/profileBlack.png';
import friendsActive from '../../assets/icons/friends.png';
import friendsInactive from '../../assets/icons/friendsBlack.png';
import gameActive from '../../assets/icons/botNav-game-active.png';
import gameInactive from '../../assets/icons/botNav-game-inActive.png';
import '../../styles/public.css';
import './PublicBottomNav.css';

/**
 * Public bottom icon nav (Urban Ink active mint plate).
 * Events = `/` + `/events/*` + `/halloween`
 * Game = `/game` fog explorer (+ `/game/collection`)
 * Places = `/places` list + `/places/:id` (not map view)
 * Map = `/places?view=map`
 * Order: Events · Game · Places · Map · Friends · Profile
 * `paperDark`: idle tabs use color PNGs (Dark Mode bar); light uses *Black / inActive.
 */
const PublicBottomNav = ({ paperDark = false }) => {
  const { pathname, search } = useLocation();
  const { unreadTotal } = useChatInbox();
  const friendsBadge = unreadBadgeLabel(unreadTotal);
  const params = new URLSearchParams(search);
  const isMapView = pathname === '/places' && params.get('view') === 'map';
  const eventsActiveRoute = pathname === '/' || pathname === '/halloween' || pathname.startsWith('/events');
  const placesActiveRoute = pathname.startsWith('/places') && !isMapView;
  const mapActiveRoute = isMapView;
  const gameActiveRoute =
    pathname === '/game' || pathname.startsWith('/game/collection');
  const profileActiveRoute = pathname.startsWith('/profile');
  // const shopActiveRoute = pathname.startsWith('/shop');
  const friendsActiveRoute = pathname.startsWith('/friends');

  const onTabClick = (tab) => (event) => {
    triggerShakeOnce(event.currentTarget);
    trackUxEvent({ name: 'nav_tab', tab });
  };

  return (
    <nav className="public-bottom-nav" aria-label="Primary">
      <div className="public-bottom-nav-inner">
        <Link
          to="/"
          className={`public-bottom-nav__tab m2b-shake-once${eventsActiveRoute ? ' is-active' : ''}`}
          aria-current={eventsActiveRoute ? 'page' : undefined}
          onClick={onTabClick('events')}
        >
          <img
            src={paperDark || eventsActiveRoute ? eventsActive : eventsInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--events"
            width={34}
            height={39}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Events</span>
        </Link>
        <Link
          to="/game"
          className={`public-bottom-nav__tab m2b-shake-once${gameActiveRoute ? ' is-active' : ''}`}
          aria-current={gameActiveRoute ? 'page' : undefined}
          onClick={onTabClick('game')}
        >
          <img
            src={paperDark || gameActiveRoute ? gameActive : gameInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--game"
            width={32}
            height={32}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Game</span>
        </Link>
        <Link
          to="/places"
          className={`public-bottom-nav__tab m2b-shake-once${placesActiveRoute ? ' is-active' : ''}`}
          aria-current={placesActiveRoute ? 'page' : undefined}
          onClick={onTabClick('places')}
        >
          <img
            src={paperDark || placesActiveRoute ? placesActive : placesInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--places"
            width={50}
            height={40}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Places</span>
        </Link>
        <Link
          to="/places?view=map"
          className={`public-bottom-nav__tab m2b-shake-once${mapActiveRoute ? ' is-active' : ''}`}
          aria-current={mapActiveRoute ? 'page' : undefined}
          onClick={onTabClick('map')}
        >
          <img
            src={paperDark || mapActiveRoute ? mapActive : mapInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--map"
            width={33}
            height={40}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Map</span>
        </Link>
        {/* Shop — temporarily hidden
        <Link
          to="/shop"
          className={`public-bottom-nav__tab m2b-shake-once${shopActiveRoute ? ' is-active' : ''}`}
          aria-current={shopActiveRoute ? 'page' : undefined}
          onClick={onTabClick('shop')}
        >
          <img
            src={shopActiveRoute ? shopActive : shopInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--shop"
            width={28}
            height={32}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Shop</span>
        </Link>
        */}
        <Link
          to="/friends"
          className={`public-bottom-nav__tab m2b-shake-once${friendsActiveRoute ? ' is-active' : ''}`}
          aria-current={friendsActiveRoute ? 'page' : undefined}
          aria-label={friendsBadge ? `Friends, ${unreadTotal} unread` : undefined}
          onClick={onTabClick('friends')}
        >
          <span className="public-bottom-nav__icon-wrap">
            <img
              src={paperDark || friendsActiveRoute ? friendsActive : friendsInactive}
              alt=""
              className="public-bottom-nav__icon public-bottom-nav__icon--friends"
              width={36}
              height={36}
              decoding="async"
            />
            {friendsBadge ? (
              <span className="public-bottom-nav__badge" aria-hidden="true">
                {friendsBadge}
              </span>
            ) : null}
          </span>
          <span className="public-bottom-nav__label">Friends</span>
        </Link>
        <Link
          to="/profile"
          className={`public-bottom-nav__tab m2b-shake-once${profileActiveRoute ? ' is-active' : ''}`}
          aria-current={profileActiveRoute ? 'page' : undefined}
          onClick={onTabClick('profile')}
        >
          <img
            src={paperDark || profileActiveRoute ? profileActive : profileInactive}
            alt=""
            className="public-bottom-nav__icon public-bottom-nav__icon--profile"
            width={40}
            height={38}
            decoding="async"
          />
          <span className="public-bottom-nav__label">Profile</span>
        </Link>
      </div>
    </nav>
  );
};

export default PublicBottomNav;
